import React, { useState } from 'react'
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material'
import {
  Calculate as CalculateIcon,
  Preview as PreviewIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import api from '../../services/api'
import { formatCurrency } from '../../utils/formatNumber'
import { extractErrorMessage } from '../../utils/errorHandler'

interface EvaluationPeriod {
  period_id: string
  period_name: string
  company_adjustment_rate: number
  target_average_increase_rate: number | null
  is_adjustment_rate_locked: boolean
  status: string
}

interface EmployeeImpact {
  employee_id: string
  name: string
  department_id: string
  department_name: string
  department_type: string
  job_grade: string
  job_title: string
  rank: string
  performance_score: number
  years_of_service: number
  current_salary_increase_rate: number
  new_salary_increase_rate: number
  change: number
}

interface DepartmentBreakdown {
  department_id: string
  department_name: string
  department_type: string
  employee_count: number
  average_current_rate: number
  average_new_rate: number
  average_change: number
}

interface PreviewData {
  period_id: string
  period_name: string
  current_adjustment_rate: number
  manual_adjustment_rate: number
  target_average_increase_rate: number | null
  summary: {
    total_employees: number
    average_current_rate: number
    average_new_rate: number
    average_change: number
    max_new_rate: number
    min_new_rate: number
    max_change: number
    min_change: number
  }
  department_breakdown: DepartmentBreakdown[]
  employee_impacts: EmployeeImpact[]
}

const STEPS = ['評価期間選択', 'R値入力', 'プレビュー確認', '確定・適用']

const RValueManagementPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')
  const [manualRValue, setManualRValue] = useState<string>('')
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)
  const [activeStep, setActiveStep] = useState(0)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  // Fetch evaluation periods
  const { data: periodsData, isLoading: periodsLoading } = useQuery({
    queryKey: ['evaluation-periods'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: EvaluationPeriod[] }>('/evaluation-periods')
      return response.data.data
    },
  })

  // Auto-calculate R value mutation
  const autoCalculateMutation = useMutation({
    mutationFn: async (periodId: string) => {
      const response = await api.post<{ success: boolean; data: any }>(
        `/evaluation-periods/${periodId}/calculate-adjustment-rate`
      )
      return response.data.data
    },
    onSuccess: (data) => {
      setManualRValue(data.required_adjustment_rate.toString())
      toast.success('自動計算が完了しました')
      setActiveStep(1)
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '自動計算に失敗しました'))
    },
  })

  // Preview manual R value mutation
  const previewMutation = useMutation({
    mutationFn: async ({ periodId, rValue }: { periodId: string; rValue: number }) => {
      const response = await api.post<{ success: boolean; data: PreviewData }>(
        `/evaluation-periods/${periodId}/preview-manual-adjustment-rate`,
        { manual_adjustment_rate: rValue }
      )
      return response.data.data
    },
    onSuccess: (data) => {
      setPreviewData(data)
      toast.success('プレビューを表示しました')
      setActiveStep(2)
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'プレビュー表示に失敗しました'))
    },
  })

  // Lock and finalize R value mutation
  const finalizeMutation = useMutation({
    mutationFn: async ({ periodId, rValue }: { periodId: string; rValue: number }) => {
      const response = await api.put<{ success: boolean; data: any }>(
        `/evaluation-periods/${periodId}/lock-adjustment-rate`,
        { company_adjustment_rate: rValue }
      )
      return response.data.data
    },
    onSuccess: () => {
      toast.success('R値を確定し、全従業員の昇給率を計算しました')
      queryClient.invalidateQueries({ queryKey: ['evaluation-periods'] })
      setActiveStep(3)
      setConfirmDialogOpen(false)
      // Reset form
      setTimeout(() => {
        handleReset()
      }, 3000)
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'R値の確定に失敗しました'))
    },
  })

  const handlePeriodSelect = (periodId: string) => {
    setSelectedPeriodId(periodId)
    setActiveStep(0)
    setPreviewData(null)
    setManualRValue('')
  }

  const handleAutoCalculate = () => {
    if (!selectedPeriodId) {
      toast.error('評価期間を選択してください')
      return
    }
    autoCalculateMutation.mutate(selectedPeriodId)
  }

  const handlePreview = () => {
    if (!selectedPeriodId || !manualRValue) {
      toast.error('評価期間とR値を入力してください')
      return
    }
    const rValue = parseFloat(manualRValue)
    if (isNaN(rValue)) {
      toast.error('有効なR値を入力してください')
      return
    }
    previewMutation.mutate({ periodId: selectedPeriodId, rValue })
  }

  const handleFinalize = () => {
    if (!selectedPeriodId || !manualRValue || !previewData) {
      toast.error('プレビューを確認してから確定してください')
      return
    }
    setConfirmDialogOpen(true)
  }

  const handleConfirmFinalize = () => {
    const rValue = parseFloat(manualRValue)
    finalizeMutation.mutate({ periodId: selectedPeriodId, rValue })
  }

  const handleReset = () => {
    setSelectedPeriodId('')
    setManualRValue('')
    setPreviewData(null)
    setActiveStep(0)
  }

  const selectedPeriod = periodsData?.find((p) => p.period_id === selectedPeriodId)

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CalculateIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            R値手入力・確定
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}>
            リセット
          </Button>
        </Box>
      </Box>

      {/* Progress Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step 1: Period Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          ステップ1: 評価期間選択
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Alert severity="info" sx={{ mb: 2 }}>
          R値（全社統一調整率）を設定する評価期間を選択してください。
        </Alert>

        {periodsLoading ? (
          <Typography>読み込み中...</Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {periodsData?.map((period) => (
              <Card
                key={period.period_id}
                sx={{
                  minWidth: 200,
                  cursor: period.is_adjustment_rate_locked ? 'not-allowed' : 'pointer',
                  border:
                    selectedPeriodId === period.period_id ? '2px solid' : '1px solid',
                  borderColor:
                    selectedPeriodId === period.period_id ? 'primary.main' : 'divider',
                  opacity: period.is_adjustment_rate_locked ? 0.6 : 1,
                }}
                onClick={() => !period.is_adjustment_rate_locked && handlePeriodSelect(period.period_id)}
              >
                <CardContent>
                  <Typography variant="h6">{period.period_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {period.period_id}
                  </Typography>
                  {period.is_adjustment_rate_locked && (
                    <Chip
                      icon={<LockIcon />}
                      label="確定済み"
                      color="success"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                  {period.target_average_increase_rate && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      目標平均: {period.target_average_increase_rate}%
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>

      {/* Step 2: R Value Input */}
      {selectedPeriodId && selectedPeriod && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            ステップ2: R値入力
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>選択中の評価期間:</strong> {selectedPeriod.period_name}
            </Typography>
            <Typography variant="body2">
              自動計算ボタンで目標平均昇給率から逆算したR値を取得できます。
              または、手動でR値を入力してください。
            </Typography>
          </Alert>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                startIcon={<CalculateIcon />}
                onClick={handleAutoCalculate}
                disabled={autoCalculateMutation.isPending}
                fullWidth
              >
                自動計算
              </Button>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="R値（手入力）"
                type="number"
                value={manualRValue}
                onChange={(e) => setManualRValue(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                fullWidth
                helperText="例: 3.5 → 3.5%"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={handlePreview}
                disabled={!manualRValue || previewMutation.isPending}
                fullWidth
              >
                プレビュー表示
              </Button>
            </Grid>
          </Grid>

          {selectedPeriod.target_average_increase_rate && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                目標平均昇給率: {selectedPeriod.target_average_increase_rate}%
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Step 3: Preview */}
      {previewData && (
        <>
          {/* Summary Statistics */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              ステップ3: プレビュー確認
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>重要:</strong>{' '}
                このプレビューはシミュレーション結果です。「確定して全体適用」ボタンを押すまでデータベースには保存されません。
              </Typography>
            </Alert>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      対象従業員数
                    </Typography>
                    <Typography variant="h4">{previewData.summary.total_employees}人</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      平均昇給率（現在）
                    </Typography>
                    <Typography variant="h4">{previewData.summary.average_current_rate}%</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      平均昇給率（新規）
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {previewData.summary.average_new_rate}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      平均変化量
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {previewData.summary.average_change > 0 ? (
                        <TrendingUpIcon color="success" />
                      ) : previewData.summary.average_change < 0 ? (
                        <TrendingDownIcon color="error" />
                      ) : null}
                      <Typography
                        variant="h4"
                        color={
                          previewData.summary.average_change > 0
                            ? 'success.main'
                            : previewData.summary.average_change < 0
                            ? 'error.main'
                            : 'text.primary'
                        }
                      >
                        {previewData.summary.average_change > 0 ? '+' : ''}
                        {previewData.summary.average_change}%
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Department Breakdown */}
            <Typography variant="h6" gutterBottom>
              部門別統計
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>部門</TableCell>
                    <TableCell align="right">従業員数</TableCell>
                    <TableCell align="right">平均昇給率（現在）</TableCell>
                    <TableCell align="right">平均昇給率（新規）</TableCell>
                    <TableCell align="right">変化量</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.department_breakdown.map((dept) => (
                    <TableRow key={dept.department_id}>
                      <TableCell>{dept.department_name}</TableCell>
                      <TableCell align="right">{dept.employee_count}人</TableCell>
                      <TableCell align="right">{dept.average_current_rate}%</TableCell>
                      <TableCell align="right">{dept.average_new_rate}%</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color:
                            dept.average_change > 0
                              ? 'success.main'
                              : dept.average_change < 0
                              ? 'error.main'
                              : 'text.primary',
                        }}
                      >
                        {dept.average_change > 0 ? '+' : ''}
                        {dept.average_change}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Employee Impact Table */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              従業員別影響一覧
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>社員ID</TableCell>
                    <TableCell>氏名</TableCell>
                    <TableCell>部門</TableCell>
                    <TableCell>職級</TableCell>
                    <TableCell>ランク</TableCell>
                    <TableCell align="right">P点</TableCell>
                    <TableCell align="right">現在昇給率</TableCell>
                    <TableCell align="right">新規昇給率</TableCell>
                    <TableCell align="right">変化量</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.employee_impacts.map((emp) => (
                    <TableRow key={emp.employee_id} hover>
                      <TableCell>{emp.employee_id}</TableCell>
                      <TableCell>{emp.name}</TableCell>
                      <TableCell>{emp.department_name}</TableCell>
                      <TableCell>{emp.job_grade}</TableCell>
                      <TableCell>
                        <Chip label={emp.rank} size="small" />
                      </TableCell>
                      <TableCell align="right">{emp.performance_score}</TableCell>
                      <TableCell align="right">{emp.current_salary_increase_rate}%</TableCell>
                      <TableCell align="right">{emp.new_salary_increase_rate}%</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color:
                            emp.change > 0
                              ? 'success.main'
                              : emp.change < 0
                              ? 'error.main'
                              : 'text.primary',
                          fontWeight: 'bold',
                        }}
                      >
                        {emp.change > 0 ? '+' : ''}
                        {emp.change}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Finalize Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant="outlined" size="large" onClick={() => setPreviewData(null)}>
              戻る
            </Button>
            <Button
              variant="contained"
              color="error"
              size="large"
              startIcon={<LockIcon />}
              onClick={handleFinalize}
              disabled={finalizeMutation.isPending}
            >
              確定して全体適用
            </Button>
          </Box>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>R値の確定</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>警告:</strong> この操作は取り消すことができません。
            </Typography>
            <Typography variant="body2">
              R値を確定すると、全従業員の昇給率が一括で計算・保存されます。
            </Typography>
          </Alert>

          <Typography variant="body1" gutterBottom>
            以下の内容で確定しますか？
          </Typography>
          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mt: 2 }}>
            <Typography variant="body2">
              <strong>評価期間:</strong> {previewData?.period_name}
            </Typography>
            <Typography variant="body2">
              <strong>R値:</strong> {manualRValue}%
            </Typography>
            <Typography variant="body2">
              <strong>対象従業員数:</strong> {previewData?.summary.total_employees}人
            </Typography>
            <Typography variant="body2">
              <strong>平均昇給率:</strong> {previewData?.summary.average_new_rate}%
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmFinalize}
            disabled={finalizeMutation.isPending}
          >
            確定する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RValueManagementPage
